import { UIView } from "uicore-ts"
import { UITooltip } from "uicore-ts/compiledScripts/UITooltip"
import { RHBadges } from "./Utils_slop/RHDesignTokens"


/**
 * RHTooltip
 *
 * App-level styled tooltip using the default plain-HTML content view.
 * Overrides applyStyles() to apply RH design tokens.
 *
 * Instantiate once at app startup:
 *   new RHTooltip()
 */
export class RHTooltip extends UITooltip {

    constructor() {
        super()
        // Roomier than the framework default (320) so a full "Company — date" row
        // is not clipped, now that each line is kept intact (see applyStyles).
        // this.maxWidth = 480
        this.maxWidth = RHBadges.tooltipMaxWidth
        UITooltip.sharedInstance = this
    }

    /**
     * A view that is hovered at the moment it is removed from the view tree —
     * e.g. a table row whose dialog is dismissed while the pointer rests on it —
     * never receives PointerLeave, because the browser does not emit
     * mouseleave/mouseout for a node detached while hovered. Without this the
     * base tooltip would stay visible and keep following the cursor for good.
     *
     * attach() therefore subscribes each anchor to its own RemovedFromViewTree
     * broadcast and hides the bubble on teardown. One stable handler reference
     * is reused for every anchor, so the repeated attach() calls the data tables
     * make on each row-data update never accumulate duplicate subscriptions, and
     * hide() is a safe no-op when nothing is showing.
     */
    readonly _hideAfterAnchorRemovedFromViewTree = (): void => {
        this.hide()
    }

    override attach(view: UIView, text: string) {
        super.attach(view, text)
        view.performFunctionAfterBroadcastEvent(
            UIView.broadcastEventName.RemovedFromViewTree,
            this._hideAfterAnchorRemovedFromViewTree
        )
    }

    override detach(view: UIView) {
        super.detach(view)
        view.removePerformFunctionAfterBroadcastEvent(
            UIView.broadcastEventName.RemovedFromViewTree,
            this._hideAfterAnchorRemovedFromViewTree
        )
    }

    /**
     * Turns label markup into clean hover-tooltip text. UIButton (and
     * CBTableRowView) fall back to the raw label text for the native title, so
     * any label that renders HTML (two-line headers, pill toggles, SVG icons)
     * would otherwise show that markup verbatim in the tooltip.
     *
     *   "Vastab hanke-<br>tingimustele"  → "Vastab hanketingimustele"
     *   "01.01.2026<br>12:00"            → "01.01.2026 12:00"
     *   "<span>Jah</span><span>Ei</span>" → "Jah Ei"
     *   "<svg …>…</svg>"                  → "" (no tooltip)
     */
    static plainTextFromMarkup(markup: string | number | undefined): string {
        // Numeric cells (e.g. turnover/employee-count columns) carry their raw
        // key-path value here — a number, which has no .replace and would crash
        // the whole row's data setter, leaving the table empty.
        if (typeof markup === "number") {
            return "" + markup
        }
        if (typeof markup !== "string" || markup.length === 0) {
            return ""
        }
        return markup
            .replace(/-<br\s*\/?>/gi, "")   // a line-break hyphen continues the word
            .replace(/<br\s*\/?>/gi, " ")   // date/time cells join the two lines with a space
            .replace(/<[^>]*>/g, " ")       // strip any other inline markup (e.g. span wrappers)
            .replace(/\s+/g, " ")           // collapse whitespace left behind by stripped tags
            .trim()
    }
    
    protected override applyStyles() {
        this.contentView.configureWithObject({
            // backgroundColor: new UIColor("rgba(28, 28, 42, 0.97)")
            backgroundColor: RHBadges.tooltipBackgroundColor,
            style: {
                // borderRadius: "6px",
                borderRadius: RHBadges.tooltipBorderRadius,
                // boxShadow: "0 4px 16px rgba(0,0,0,0.28), 0 1px 4px rgba(0,0,0,0.18)",
                boxShadow: RHBadges.tooltipBoxShadow,
                // border: "1px solid rgba(255,255,255,0.08)"
                border: RHBadges.tooltipBorder
            }
        })

        if (this._labelElement) {
            // this._labelElement.style.color = "#f0f0f5"
            this._labelElement.style.color = RHBadges.tooltipTextColor
            // this._labelElement.style.fontSize = "14px"
            this._labelElement.style.fontSize = RHBadges.tooltipFontSize
            // this._labelElement.style.fontWeight = "400"
            this._labelElement.style.fontWeight = RHBadges.tooltipFontWeight
            // this._labelElement.style.lineHeight = "1.6"
            this._labelElement.style.lineHeight = RHBadges.tooltipLineHeight
            // The framework's default content label uses white-space: pre-wrap +
            // word-break: break-word. During sizing the tooltip's container has no
            // defined width yet, so that pair makes the label measure its
            // min-content — one character per line — and the tooltip renders as a
            // 1-char-wide vertical strip. Our rows are short "Company — date" lines,
            // so keep each line intact (pre); the longest line then drives the width.
            // this._labelElement.style.whiteSpace = "pre"
            this._labelElement.style.whiteSpace = RHBadges.tooltipWhiteSpace
            // this._labelElement.style.wordBreak = "normal"
            this._labelElement.style.wordBreak = RHBadges.tooltipWordBreak
            // this._labelElement.style.overflowWrap = "normal"
            this._labelElement.style.overflowWrap = RHBadges.tooltipOverflowWrap
            // Cap the wrap width on the label itself and let it size to its content (but
            // no wider than maxWidth). With white-space: pre-wrap this makes a long line
            // wrap at maxWidth while a short "Company — date" block keeps its natural
            // width — and it avoids the 1-char-per-line min-content collapse.
            this._labelElement.style.maxWidth = RHBadges.tooltipMaxWidth + "px"
            this._labelElement.style.width = RHBadges.tooltipLabelWidth
        }
    }

    /**
     * Sizes the bubble to its content. The base implementation measures with
     * `width: fit-content`, which depends on the available width — but at measure
     * time the bubble has no width set yet, so it collapses to min-content (one
     * word per line) and the dark container comes out far too narrow while the
     * label (kept at `max-content`, see applyStyles) overflows it.
     *
     * We instead measure the label exactly as it is displayed: `width: max-content`
     * is intrinsic (independent of the unsized parent), capped by `maxWidth`, with
     * `white-space: pre-wrap`. So a short "Company — date" row sizes to that row, a
     * multi-line block sizes to its longest line, and an over-long single line wraps
     * at `maxWidth` — the bubble always matches its text. scrollWidth/scrollHeight
     * already include the label's border-box padding.
     */
    protected override measureContentSize(): { width: number, height: number } {
        const label = this._labelElement
        if (!label) {
            return super.measureContentSize()
        }
        const wasDetached = !label.isConnected
        if (wasDetached) {
            document.body.appendChild(label)
        }
        const width = Math.min(Math.ceil(label.scrollWidth), this.maxWidth)
        const height = Math.ceil(label.scrollHeight)
        if (wasDetached) {
            document.body.removeChild(label)
        }
        return { width, height }
    }

}
